import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import ImageUploader from "../../../components/base/ImageUploader";
import RichTextEditor from "../../../components/base/RichTextEditor";
import { supabase } from "../../../lib/supabase";
import { sanitizeInterviewHtml } from "../../../lib/sanitizeWordHtml";
import { formatDateForDisplay, formatDateForInput } from "../../../lib/dateHelpers";

/* ─── helpers ─── */
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function getReadTime(content: string): string {
  const text = content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const words = text ? text.split(" ").length : 0;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min`;
}

/* ─── types ─── */
interface StatsArticle {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  slug: string;
  date: string;
  read_time: string;
  category: string;
  category_color: string;
  author: string;
  featured: boolean;
  tags: string[];
  views?: number;
  created_at: string;
  updated_at: string;
}

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=dramatic%20wide%20shot%20of%20a%20modern%20commercial%20airplane%20taking%20off%20from%20a%20Caribbean%20airport%20with%20blue%20sky%20and%20tropical%20clouds%20aviation%20photography%20high%20quality%20editorial&width=1200&height=675&seq=stats-article-fallback&orientation=landscape";

export default function StatsNewsEditor() {
  const [articles, setArticles] = useState<StatsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [publishedDate, setPublishedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  /* ─── fetch ─── */
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from("articles")
        .select("*")
        .eq("category", "Estadísticas")
        .order("created_at", { ascending: false });

      if (err) throw err;
      setArticles(data || []);
    } catch (err) {
      setMessage(
        "Error al cargar: " +
          (err instanceof Error ? err.message : "Error desconocido")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  /* ─── save ─── */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setMessage("Error: El título es obligatorio.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const dataToSave = {
        title: title.trim(),
        excerpt: excerpt.trim() || title.trim(),
        content: sanitizeInterviewHtml(content),
        category: "Estadísticas",
        category_color: "bg-blue-600",
        author: "Aviación Insight RD",
        date: formatDateForDisplay(publishedDate),
        read_time: getReadTime(content),
        image: image.trim() || FALLBACK_IMAGE,
        featured: false,
        ...(editingId ? {} : { slug: generateSlug(title) }),
        tags: ["Estadísticas"],
      };

      if (editingId) {
        const { error } = await supabase
          .from("articles")
          .update(dataToSave)
          .eq("id", editingId);
        if (error) throw error;
        setMessage("Noticia actualizada correctamente.");
      } else {
        const { error } = await supabase.from("articles").insert([dataToSave]);
        if (error) throw error;
        setMessage("Noticia publicada correctamente.");
      }
      handleClear();
      fetchArticles();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Error desconocido al guardar";
      console.error("Stats save error:", msg);
      setMessage("Error: " + msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: StatsArticle) => {
    setEditingId(item.id);
    setTitle(item.title);
    setExcerpt(item.excerpt);
    setContent(sanitizeInterviewHtml(item.content));
    setImage(item.image);
    setPublishedDate(formatDateForInput(item.date));
    setMessage("");
    setDeleteConfirm(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClear = () => {
    setEditingId(null);
    setTitle("");
    setExcerpt("");
    setContent("");
    setImage("");
    setPublishedDate(new Date().toISOString().split("T")[0]);
    setMessage("");
    setDeleteConfirm(null);
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      const { error } = await supabase.from("articles").delete().eq("id", id);
      if (error) throw error;
      setMessage("Noticia eliminada.");
      setDeleteConfirm(null);
      fetchArticles();
    } catch (err) {
      setMessage(
        "Error: " + (err instanceof Error ? err.message : "Error desconocido")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Feedback */}
      {message && (
        <div
          className={`flex items-center gap-2 rounded-xl px-4 py-3 ${
            message.includes("Error")
              ? "bg-red-50 border border-red-200 text-red-600"
              : "bg-emerald-50 border border-emerald-200 text-emerald-600"
          }`}
        >
          <i
            className={
              message.includes("Error")
                ? "ri-error-warning-line"
                : "ri-check-line"
            }
          />
          <p className="text-sm font-semibold">{message}</p>
        </div>
      )}

      {/* Editor form */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="bg-[#0B1F3A] px-6 py-4 flex items-center justify-between">
          <h2
            className="text-white font-bold text-sm"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            <i className="ri-edit-line mr-1.5" />
            {editingId ? "Editar Noticia" : "Nueva Noticia de Estadísticas"}
          </h2>
          {editingId && (
            <button
              onClick={handleClear}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1" /> Nueva noticia
            </button>
          )}
        </div>
        <form onSubmit={handleSave} className="px-6 py-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                Título *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Tráfico aerocomercial crece 18% en el primer trimestre"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                Fecha de publicación
              </label>
              <input
                type="date"
                value={publishedDate}
                onChange={(e) => setPublishedDate(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
              />
            </div>
          </div>

          <ImageUploader
            value={image}
            onChange={setImage}
            label="Imagen de la noticia"
            folder="articles"
            cropTarget="articleHero"
          />

          <div>
            <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
              Extracto (resumen corto)
            </label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Breve resumen de 2-3 líneas sobre los datos o cifras clave..."
              rows={2}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all resize-none"
            />
          </div>

          <div>
            <RichTextEditor
              label="Contenido completo"
              required
              value={content}
              onChange={setContent}
              placeholder="Contenido completo con los datos, análisis y contexto de las estadísticas..."
              minHeight="280px"
              allowHtmlPaste={true}
            />
            <p className="text-slate-400 text-[10px] mt-1">
              Da formato con la barra flotante (negritas, viñetas) e inserta imágenes con la barra superior. Tiempo de lectura estimado: {getReadTime(content)}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-[#0B1F3A] hover:bg-slate-800 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-save-line" />
              )}
              {editingId ? "Guardar cambios" : "Publicar noticia"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={handleClear}
                className="px-6 py-3 border border-slate-200 text-slate-600 font-semibold text-sm rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Articles list */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2
            className="text-[#0B1F3A] font-bold text-sm"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Noticias publicadas ({articles.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl mx-auto mb-3">
              <i className="ri-file-text-line text-slate-400 text-2xl" />
            </div>
            <p className="text-slate-400 text-sm">
              Aún no hay noticias de estadísticas.
            </p>
            <p className="text-slate-300 text-xs mt-1">
              Redacta una noticia arriba y aparecerá aquí y en la página de
              estadísticas del portal.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {articles.map((item) => (
              <div
                key={item.id}
                className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <i className="ri-file-text-line text-slate-400 text-lg" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p
                      className="text-[#0B1F3A] font-semibold text-sm truncate"
                      style={{ fontFamily: "'Sora', sans-serif" }}
                    >
                      {item.title}
                    </p>
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">
                      Estadísticas
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs truncate">
                    {item.excerpt || "Sin extracto"}
                  </p>
                  <p className="text-slate-300 text-[10px] mt-0.5">
                    {item.date} · {item.read_time}
                    {item.views !== undefined && item.views > 0
                      ? ` · ${item.views} lecturas`
                      : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link
                    to={`/articulo/${item.slug}`}
                    target="_blank"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors cursor-pointer"
                    title="Ver artículo"
                  >
                    <i className="ri-eye-line text-sm" />
                  </Link>
                  <button
                    onClick={() => handleEdit(item)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer"
                    title="Editar"
                  >
                    <i className="ri-pencil-line text-sm" />
                  </button>
                  {deleteConfirm === item.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
                      >
                        Confirmar
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1 bg-slate-200 text-slate-600 text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(item.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}